import {
  CalendarToday as CalendarTodayIcon,
  Equalizer as EqualizerIcon,
  EventNote as EventNoteIcon,
  Group as GroupIcon,
  LocalHospital as LocalHospitalIcon,
  PendingActions as PendingActionsIcon,
  TrendingUp as TrendingUpIcon,
  Vaccines as VaccinesIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { Fragment, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import SimpleIconAlert from "../../../../components/display/SimpleIconAlert";
import TitleWithIcon from "../../../../components/display/TitleWithIcon";
import { useFetch } from "../../../../hooks/Fetching";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import usePrimaryColor from "../../../../hooks/usePrimaryColor";
import { useIsTablet } from "../../../../utils/Functions";

// Styled component for animated stat cards
const StatCard = styled(Box)(({ theme, color }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  borderTop: `2px solid ${theme.palette[color].main}`,
  borderBottom: `2px solid ${theme.palette[color].main}`,
  padding: theme.spacing(3),
  height: "100%",
  textAlign: "center",
  transition: "all 0.3s ease",
  position: "relative",
  overflow: "hidden",
}));

// Configuration for stat cards
const STAT_ITEMS_CONFIG = (stats) => [
  {
    id: "activeAppointments",
    label: "ActiveAppointments",
    value: stats?.activeAppointments,
    icon: <EventNoteIcon fontSize="large" />,
    color: "primary",
  },
  {
    id: "sickPets",
    label: "SickPets",
    value: stats?.sickPets,
    icon: <LocalHospitalIcon fontSize="large" />,
    color: "error",
  },
  {
    id: "activeUsers",
    label: "ActiveUsers",
    value: stats?.activeUsers,
    icon: <GroupIcon fontSize="large" />,
    color: "info",
  },
  {
    id: "pendingAppointments",
    label: "PendingAppointments",
    value: stats?.pendingAppointments,
    icon: <PendingActionsIcon fontSize="large" />,
    color: "warning",
  },
  {
    id: "vaccinatedPets",
    label: "VaccinatedPets",
    value: stats?.vaccinatedPets,
    icon: <VaccinesIcon fontSize="large" />,
    color: "success",
  },
];

const TIME_VIEW_OPTIONS = [
  { value: "day", label: "Today", icon: <CalendarTodayIcon /> },
  { value: "week", label: "ThisWeek", icon: <EqualizerIcon /> },
  { value: "month", label: "ThisMonth", icon: <TrendingUpIcon /> },
  { value: "year", label: "ThisYear", icon: <EqualizerIcon /> },
];

const ls = "statsView";

const ClinicStatisticsDashboard = () => {
  const { mode } = useContext(ThemeContext);
  const color = usePrimaryColor();
  const light = String(mode) === "light";
  const [searchParams, setSearchParams] = useSearchParams();
  let param = searchParams.get("statsView") || "day";
  let apiPath = `/clinics/homepage/information?statisticsView=${param}`;
  const { data, error, isLoading } = useFetch(apiPath, null, [
    "clinicInformation",
    param,
  ]);

  const isTablet = useIsTablet();
  const { t } = useTranslation();
  const theme = useTheme();
  const [timeView, setTimeView] = useState(param);

  const handleViewChange = (_, newView) => {
    if (newView) {
      setTimeView(newView);
      searchParams.set("statsView", newView);
      setSearchParams(searchParams);
      localStorage.setItem(ls, String(newView));
    }
  };

  React.useEffect(() => {
    const existingValue = localStorage.getItem(ls);
    if (existingValue) {
      handleViewChange("", existingValue);
    }
  }, []);

  const statItems = STAT_ITEMS_CONFIG(data);

  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: theme.shadows[2],
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: light ? "white" : "",
      }}
    >
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: isTablet ? "column" : "row",
          alignItems: isTablet ? "flex-start" : "center",
          gap: isTablet ? 3 : 0,
        }}
        title={
          <TitleWithIcon title={"ClinicInformation"}>
            <EqualizerIcon />
          </TitleWithIcon>
        }
        action={
          <ToggleButtonGroup
            value={timeView}
            exclusive
            onChange={handleViewChange}
            size="medium"
            sx={{
              gap: 2,
              "& .MuiToggleButton-root": {
                px: 2,
                border: "none",
                "&:not(:first-of-type)": {
                  borderRadius: theme.shape.borderRadius,
                },
                "&:first-of-type": {
                  borderRadius: theme.shape.borderRadius,
                },
              },
            }}
          >
            {TIME_VIEW_OPTIONS.map((option) => (
              <>
                <ToggleButton
                  value={option.value}
                  sx={{
                    textTransform: "capitalize",
                    "&.Mui-selected": {
                      backgroundColor: color,
                      color: theme.palette.primary.contrastText,
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    {option.icon && (
                      <Box mr={1} display="flex">
                        {option.icon}
                      </Box>
                    )}
                    {t(option.label)}
                  </Box>
                </ToggleButton>
              </>
            ))}
          </ToggleButtonGroup>
        }
      />

      <CardContent sx={{ pt: 3, px: 4, pb: 4 }}>
        {error ? (
          <SimpleIconAlert message={"ErrorFetchingClinicInformation"} />
        ) : (
          <Grid container spacing={3}>
            {statItems.map((item, index) => (
              <Grid
                key={item.id}
                item
                xs={12}
                sm={12}
                md={index == statItems.length - 1 ? 12 : 6}
                lg={2.4}
              >
                {isLoading ? (
                  <Skeleton height={250} />
                ) : (
                  <Fragment>
                    <StatCard color={item.color}>
                      <Box
                        sx={{
                          color: theme.palette[item.color].main,
                          mb: 2,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        mb={1}
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {t(item.label)}
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        mb={1}
                        sx={{
                          color: theme.palette[item.color].main,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </StatCard>
                  </Fragment>
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default ClinicStatisticsDashboard;
